import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { GisLayer } from '@citizen-walk/shared-types';

/**
 * GIS レイヤーのオフラインキャッシュ層 (docs/22 §8 / docs/98 R-50、PMTiles の `offlineMap.ts` ミラー)。
 *
 * 背景: GIS overlay は通常 Firestore (`gisLayers` メタ) + Storage (`gis/{id}.geojson` 本体) から
 * オンライン取得する。オフライン (圏外 / 機内) では Storage fetch が失敗し overlay が空になる。
 * PMTiles と同様に「管理者が公開した GIS データを端末へ明示ダウンロード → IndexedDB に永続 →
 * オフラインでも重畳表示」できるようにするのが本モジュール。
 *
 * PMTiles (`offlineMap.ts`、store=offlineMaps、key=districtId、Blob 1 本) との違い:
 *   - GIS は 1 地区に複数レイヤー → entry はレイヤー配列を持つ
 *   - 各レイヤーは **メタ snapshot (style/name/順序/属性スキーマ) + GeoJSON 本文** を併せて保存する。
 *     オフライン時は Firestore `onSnapshot` がメタを配れない場合があるため、描画に必要なメタも
 *     キャッシュしておくことでネット非依存で overlay を再構成できる。
 *
 * firebase 非依存 (テスト容易性 + offlineMap.ts と同方針)。Storage からの取得はページ側が
 * `fetchGeoJson` として注入する (MapDownloadPage が getBytes で実装)。
 *
 * 提供 API:
 *   - `getOfflineGisEntry` / `putOfflineGisEntry` / `deleteOfflineGisEntry` / `listOfflineGisEntries`
 *   - `gisLayersSignature(layers)`: 鮮度判定用署名 (id+updatedAt+sizeBytes)。再インポート / 編集を検知。
 *   - `buildOfflineGisEntry(districtId, layers, fetchGeoJson, onProgress)`: 全レイヤー DL → entry 構築
 */

/** オフライン保存する 1 レイヤー (メタ snapshot + GeoJSON 本文)。 */
export interface OfflineGisLayer {
  /** GisLayer メタの snapshot (style/name/geometryType/displayOrder/defaultVisible/属性/storagePath)。 */
  meta: GisLayer;
  /** GeoJSON FeatureCollection の文字列 (描画時に JSON.parse)。 */
  geojson: string;
}

/** 1 地区分のオフライン GIS entry (key=districtId)。 */
export interface OfflineGisEntry {
  districtId: string;
  /** ダウンロード済レイヤー (displayOrder 順は描画側で解決)。 */
  layers: OfflineGisLayer[];
  /** 合計バイト数 (UI 表示 + 集計用)。 */
  totalBytes: number;
  /** 取得時点の gisLayers 署名 (鮮度判定。最新と不一致なら「再ダウンロード」を促す)。 */
  signature: string;
  /** user がローカル DL 完了した日時 (ISO8601)。 */
  downloadedAt: string;
}

interface OfflineGisDBSchema extends DBSchema {
  offlineGisDistricts: {
    key: string; // districtId
    value: OfflineGisEntry;
  };
}

const DB_NAME = 'citizen-walk-offline-gis';
const DB_VERSION = 1;
const STORE = 'offlineGisDistricts';

/** put / delete を同一タブの他 hook (地図 overlay) に伝播するイベント (overrides の流儀踏襲)。 */
export const GIS_OFFLINE_EVENT = 'cw-gis-offline-changed';

let dbPromise: Promise<IDBPDatabase<OfflineGisDBSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<OfflineGisDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineGisDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

function emitChanged(districtId: string): void {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(GIS_OFFLINE_EVENT, { detail: { districtId } }));
    } catch {
      /* CustomEvent 不可環境は無視 */
    }
  }
}

export async function getOfflineGisEntry(districtId: string): Promise<OfflineGisEntry | undefined> {
  const db = await getDb();
  return db.get(STORE, districtId);
}

export async function putOfflineGisEntry(
  districtId: string,
  entry: OfflineGisEntry,
): Promise<void> {
  const db = await getDb();
  await db.put(STORE, entry, districtId);
  emitChanged(districtId);
}

export async function deleteOfflineGisEntry(districtId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, districtId);
  emitChanged(districtId);
}

export async function listOfflineGisEntries(): Promise<
  Array<{ districtId: string; entry: OfflineGisEntry }>
> {
  const db = await getDb();
  const tx = db.transaction(STORE, 'readonly');
  const store = tx.objectStore(STORE);
  const keys = (await store.getAllKeys()) as string[];
  const values = await store.getAll();
  const result: Array<{ districtId: string; entry: OfflineGisEntry }> = [];
  for (let i = 0; i < keys.length; i += 1) {
    const entry = values[i];
    if (entry) result.push({ districtId: keys[i]!, entry });
  }
  return result;
}

/**
 * gisLayers の鮮度判定用署名。レイヤー id + updatedAt + sizeBytes を昇順連結。
 * 管理者の再インポート (id/sizeBytes 変化) やスタイル/名称編集 (updatedAt 変化) を検知し、
 * cache が古いことを UI で示す (PMTiles の storagePath+generatedAt 一致判定に相当)。
 */
export function gisLayersSignature(layers: GisLayer[]): string {
  return layers
    .map((l) => `${l.id}:${l.updatedAt}:${l.sizeBytes}`)
    .sort()
    .join('|');
}

/**
 * 地区の全 GIS レイヤーをダウンロードして OfflineGisEntry を構築する (永続化は呼び出し側 putOfflineGisEntry)。
 *
 * @param districtId   対象地区
 * @param layers       gisLayers メタ一覧 (全レイヤー。表示 OFF も含め DL し、オフラインでも切替可能にする)
 * @param fetchGeoJson storagePath → GeoJSON 文字列 を返す関数 (ページが getBytes で注入)
 * @param onProgress   進捗 (完了レイヤー数 / 総数、UI bar 用)
 */
export async function buildOfflineGisEntry(
  districtId: string,
  layers: GisLayer[],
  fetchGeoJson: (storagePath: string) => Promise<string>,
  onProgress?: (done: number, total: number) => void,
): Promise<OfflineGisEntry> {
  const offlineLayers: OfflineGisLayer[] = [];
  let totalBytes = 0;
  for (let i = 0; i < layers.length; i += 1) {
    const meta = layers[i]!;
    const geojson = await fetchGeoJson(meta.storagePath);
    offlineLayers.push({ meta, geojson });
    // 実バイト数 (UTF-8) を計上。meta.sizeBytes は import 時の概算のため実測を優先。
    totalBytes += new TextEncoder().encode(geojson).length;
    onProgress?.(i + 1, layers.length);
  }
  return {
    districtId,
    layers: offlineLayers,
    totalBytes,
    signature: gisLayersSignature(layers),
    downloadedAt: new Date().toISOString(),
  };
}
